import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Save, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { applyIncrementalUpdate, getProcessMapsForProject, type ProcessMapChange, type IncrementalUpdateRequest } from '../../api';
import toast from 'react-hot-toast';

interface ProcessStep {
  id: string;
  title: string;
  description?: string;
  category?: string;
  step_number?: string;
}

interface IncrementalUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  processMapId: number;
  projectId: number;
  onUpdateSuccess?: () => void;
}

const IncrementalUpdateModal: React.FC<IncrementalUpdateModalProps> = ({
  isOpen,
  onClose,
  processMapId,
  projectId,
  onUpdateSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
  const [changes, setChanges] = useState<ProcessMapChange[]>([]);
  const [preserveValidated, setPreserveValidated] = useState(true);
  const [autoSyncSOP, setAutoSyncSOP] = useState(true);

  // Load process map data when modal opens
  useEffect(() => {
    if (isOpen && processMapId) {
      loadProcessMapData();
    }
  }, [isOpen, processMapId]);

  const loadProcessMapData = async () => {
    try {
      setLoading(true);
      const response = await getProcessMapsForProject(projectId.toString());

      console.log('🔍 [IncrementalUpdate] Available process maps:', response.process_maps?.length);
      console.log('🔍 [IncrementalUpdate] Looking for processMapId:', processMapId);

      // Find the current process map
      const currentMap = response.process_maps?.find((map: any) => map.id === processMapId);

      if (!currentMap) {
        console.error('❌ [IncrementalUpdate] Process map not found with ID:', processMapId);
        toast.error(`Process map with ID ${processMapId} not found`);
        return;
      }

      console.log('✅ [IncrementalUpdate] Found process map:', {
        id: currentMap.id,
        level: currentMap.level,
        title: currentMap.title || currentMap.process_name,
        hasMapData: !!currentMap.map_data,
        hasElements: !!currentMap.map_data?.elements,
        hasProcesses: !!currentMap.map_data?.elements?.processes,
        processCount: currentMap.map_data?.elements?.processes?.length || 0
      });

      // Check if this is a Level 3 process map (recommended for incremental updates)
      if (currentMap.level !== 3) {
        console.warn(`⚠️ [IncrementalUpdate] Process map level is ${currentMap.level}, Level 3 recommended for detailed incremental updates`);
      }

      if (currentMap.map_data?.elements?.processes && currentMap.map_data.elements.processes.length > 0) {
        console.log(`✅ [IncrementalUpdate] Loading ${currentMap.map_data.elements.processes.length} process steps`);

        // Map the backend process data to our interface format
        const mappedSteps = currentMap.map_data.elements.processes.map((process: any, index: number) => ({
          id: process.id || `activity_${index + 1}`,
          title: process.name || process.step_name || process.title || process.description || `Step ${index + 1}`,
          description: process.detailed_description || process.description || '',
          category: process.action_type || process.category || 'process',
          step_number: process.step_number || `${index + 1}`
        }));

        console.log('🔍 [IncrementalUpdate] Mapped process steps:', mappedSteps.slice(0, 3)); // Log first 3 for debugging
        setProcessSteps(mappedSteps);
      } else {
        // More detailed error message
        const errorDetails = [];
        if (!currentMap.map_data) {
          errorDetails.push('map_data is missing');
        } else if (!currentMap.map_data.elements) {
          errorDetails.push('map_data.elements is missing');
        } else if (!currentMap.map_data.elements.processes) {
          errorDetails.push('map_data.elements.processes is missing');
        } else if (currentMap.map_data.elements.processes.length === 0) {
          errorDetails.push('processes array is empty');
        }

        const errorMsg = `No process steps found in process map (Level ${currentMap.level}). Issues: ${errorDetails.join(', ')}. Try regenerating the process map or use a Level 3 process map for detailed steps.`;
        console.error('❌ [IncrementalUpdate]', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      console.error('❌ [IncrementalUpdate] Failed to load process map data:', error);
      toast.error('Failed to load process map data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const addModifyChange = (stepId: string, field: string, newValue: string) => {
    const newChange: ProcessMapChange = {
      type: 'modify_step',
      step_id: stepId,
      field,
      new_value: newValue
    };

    // Remove any existing change for the same step and field
    const filteredChanges = changes.filter(c =>
      !(c.type === 'modify_step' && c.step_id === stepId && c.field === field)
    );

    setChanges([...filteredChanges, newChange]);
  };

  const addNewStepChange = (insertAfter?: string) => {
    const newStepId = `step_${Date.now()}`;
    const newChange: ProcessMapChange = {
      type: 'add_step',
      new_step: {
        id: newStepId,
        title: 'New Step',
        description: 'Description for new step',
        category: 'process'
      },
      insert_after: insertAfter
    };

    setChanges([...changes, newChange]);
  };

  const addDeleteChange = (stepId: string) => {
    const newChange: ProcessMapChange = {
      type: 'delete_step',
      step_id: stepId
    };

    // Remove any existing changes for this step
    const filteredChanges = changes.filter(c => c.step_id !== stepId);
    setChanges([...filteredChanges, newChange]);
  };

  const removeChange = (index: number) => {
    const newChanges = [...changes];
    newChanges.splice(index, 1);
    setChanges(newChanges);
  };

  const updateChangeValue = (index: number, field: string, value: any) => {
    const newChanges = [...changes];
    if (field === 'new_step_title' && newChanges[index].new_step) {
      newChanges[index].new_step!.title = value;
    } else if (field === 'new_step_description' && newChanges[index].new_step) {
      newChanges[index].new_step!.description = value;
    } else if (field === 'new_value') {
      newChanges[index].new_value = value;
    }
    setChanges(newChanges);
  };

  const applyChanges = async () => {
    if (changes.length === 0) {
      toast.error('No changes to apply');
      return;
    }

    try {
      setSaving(true);

      const request: IncrementalUpdateRequest = {
        changes,
        preserve_validated: preserveValidated,
        auto_sync_sop: autoSyncSOP
      };

      const response = await applyIncrementalUpdate(processMapId.toString(), request);

      if (response.success) {
        toast.success(
          `✅ Applied ${response.changes_applied} changes successfully!` +
          (response.changes_failed > 0 ? ` (${response.changes_failed} failed)` : '')
        );

        setChanges([]);

        // Dispatch event for process map updates
        window.dispatchEvent(new CustomEvent('processMapUpdated', {
          detail: {
            processMapId: processMapId.toString(),
            projectId,
            changesApplied: response.changes_applied,
            timestamp: new Date().toISOString(),
            triggerSource: 'IncrementalUpdateModal'
          }
        }));

        onUpdateSuccess?.();
        onClose();
      } else {
        toast.error('Failed to apply changes');
      }
    } catch (error: any) {
      toast.error('Failed to apply changes: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getChangeDescription = (change: ProcessMapChange) => {
    switch (change.type) {
      case 'modify_step':
        return `Modify ${change.field} of "${processSteps.find(s => s.id === change.step_id)?.title || change.step_id}"`;
      case 'add_step':
        return `Add new step: "${change.new_step?.title}"`;
      case 'delete_step':
        return `Delete step: "${processSteps.find(s => s.id === change.step_id)?.title || change.step_id}"`;
      default:
        return 'Unknown change';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Edit2 className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Incremental Process Map Update</h2>
              <p className="text-sm text-gray-600">
                Make targeted changes to specific process steps without full regeneration
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {loading ? (
            <div className="flex items-center justify-center w-full flex-1">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading process map data...</span>
            </div>
          ) : (
            <>
              {/* Left Panel - Process Steps */}
              <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Process Steps</h3>
                    <button
                      onClick={() => addNewStepChange()}
                      className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Step</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {processSteps.map((step) => (
                      <div key={step.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{step.title}</div>
                            {step.description && (
                              <div className="text-sm text-gray-600 mt-1">{step.description}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-2">
                              ID: {step.id} | Category: {step.category || 'process'}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            {/* Quick Edit Title */}
                            <button
                              onClick={() => {
                                const newTitle = prompt('New title:', step.title);
                                if (newTitle && newTitle !== step.title) {
                                  addModifyChange(step.id, 'title', newTitle);
                                }
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit Title"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>

                            {/* Add Step After */}
                            <button
                              onClick={() => addNewStepChange(step.id)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Add Step After"
                            >
                              <Plus className="w-4 h-4" />
                            </button>

                            {/* Delete Step */}
                            <button
                              onClick={() => {
                                if (confirm(`Delete step "${step.title}"?`)) {
                                  addDeleteChange(step.id);
                                }
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete Step"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Panel - Changes Queue */}
              <div className="w-1/2 overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Pending Changes ({changes.length})
                    </h3>
                    {changes.length > 0 && (
                      <button
                        onClick={() => setChanges([])}
                        className="flex items-center space-x-2 px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors text-sm"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Clear All</span>
                      </button>
                    )}
                  </div>

                  {changes.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No changes queued</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Use the controls on the left to modify process steps
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {changes.map((change, index) => (
                        <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-blue-900 mb-2">
                                {getChangeDescription(change)}
                              </div>

                              {/* Editable fields for changes */}
                              {change.type === 'modify_step' && (
                                <div className="space-y-2">
                                  <div>
                                    <label className="block text-xs text-blue-700 mb-1">New Value:</label>
                                    <input
                                      type="text"
                                      value={change.new_value || ''}
                                      onChange={(e) => updateChangeValue(index, 'new_value', e.target.value)}
                                      className="w-full px-3 py-2 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder={`Enter new ${change.field}`}
                                    />
                                  </div>
                                </div>
                              )}

                              {change.type === 'add_step' && change.new_step && (
                                <div className="space-y-2">
                                  <div>
                                    <label className="block text-xs text-blue-700 mb-1">Title:</label>
                                    <input
                                      type="text"
                                      value={change.new_step.title}
                                      onChange={(e) => updateChangeValue(index, 'new_step_title', e.target.value)}
                                      className="w-full px-3 py-2 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Step title"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-blue-700 mb-1">Description:</label>
                                    <textarea
                                      value={change.new_step.description || ''}
                                      onChange={(e) => updateChangeValue(index, 'new_step_description', e.target.value)}
                                      rows={2}
                                      className="w-full px-3 py-2 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Step description"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <button
                              onClick={() => removeChange(index)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors ml-3"
                              title="Remove Change"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preserveValidated}
                  onChange={(e) => setPreserveValidated(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Preserve validated content</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={autoSyncSOP}
                  onChange={(e) => setAutoSyncSOP(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Auto-sync SOP</span>
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={applyChanges}
                disabled={changes.length === 0 || saving}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Applying Changes...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Apply Changes ({changes.length})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncrementalUpdateModal;